import { useTranslation } from "react-i18next";
import Modal from "../../common/Modal/Modal";
import Badge from "../../common/Badge/Badge";
import { badgeValidacion } from "../../common/ValidacionesHistorico/ValidacionesHistorico";
import { formatDate } from "../../../utils/utils";
import {
    formatPesoResiduo,
    pesoEnUnidadResiduo,
    unidadResiduo,
} from "../../../utils/format";
import "./ResiduoDetalleModal.scss";

const VACIO = "—";

/* Los catálogos vienen de mapList, así que cada opción es {value, label, id}. */
const etiqueta = (lista, cod) => {
    if (cod == null || !Array.isArray(lista)) return null;

    const opcion = lista.find(
        (o) => String(o.value ?? o.id) === String(cod)
    );

    return opcion?.label ?? null;
};

const texto = (valor) => {
    if (valor == null) return VACIO;

    const limpio = String(valor).trim();

    return limpio === "" ? VACIO : limpio;
};

/* Igual que la columna "document" de Table.tsx: el backend manda el fichero en
   base64 (no se sirve wwwroot como estatico), así que el link se abre con un
   data URI y se descarga con el nombre original. */
const documento = (nombre, base64) => {
    const limpio = texto(nombre);
    if (limpio === VACIO || !base64) return limpio;

    return (
        <a
            href={`data:application/pdf;base64,${base64}`}
            target="_blank"
            rel="noopener noreferrer"
            download={limpio}
        >
            {limpio}
        </a>
    );
};

const fecha = (valor) => {
    if (!valor) return VACIO;

    const formateada = formatDate(valor);

    return formateada === "N/A" ? VACIO : formateada;
};

const numero = (valor, sufijo = "") => {
    if (valor == null || valor === "") return VACIO;

    const num = Number(valor);
    if (!Number.isFinite(num)) return VACIO;

    return `${new Intl.NumberFormat("es-ES", {
        maximumFractionDigits: 2,
    }).format(num)}${sufijo}`;
};

const nombreTratamiento = (tratamiento) => {
    if (!tratamiento?.codTipoTratamiento) return VACIO;

    /* El tratamiento se identifica por su código (D5, R3...) y se describe con
       tipo y descripción; se muestra lo que haya. */
    const partes = [
        tratamiento.codigo,
        tratamiento.descripcion || tratamiento.subtipo || tratamiento.tipo,
    ].filter((p) => p && String(p).trim() !== "");

    return partes.length > 0 ? partes.join(" - ") : VACIO;
};

const Campo = ({ label, children, ancho }) => (
    <div className={`residuo-detalle__campo${ancho ? " residuo-detalle__campo--ancho" : ""}`}>
        <span className="residuo-detalle__campo-label">{label}</span>
        <span className="residuo-detalle__campo-valor">{children}</span>
    </div>
);

const Seccion = ({ titulo, children }) => (
    <section className="residuo-detalle__seccion">
        <h3 className="residuo-detalle__seccion-titulo">{titulo}</h3>
        <div className="residuo-detalle__campos">{children}</div>
    </section>
);

/**
 * Ficha de solo lectura de una línea de residuo de un centro productor: todo lo
 * que tiene metido, que es bastante más de lo que caben en la tabla.
 *
 * Los importes llegan ya convertidos a la moneda del selector desde GetObra; el
 * formateador se recibe de la ficha para no repetir aquí esa decisión.
 */
export default function ResiduoDetalleModal({
    isOpen,
    onClose,
    residuo,
    catalogos = {},
    formateaCoste,
}) {
    const { t } = useTranslation();
    if (!residuo) return null;

    const { proyectos, vehiculos, combustibles, tiposTratamiento } = catalogos;

    const unidad = unidadResiduo(residuo.codTipoResiduo);
    const esPeligroso = unidad === "kg";

    const importe = (valor) =>
        valor == null ? VACIO : formateaCoste.format(valor);

    /* El peso viaja en kg y se muestra en la unidad del tipo de residuo, igual
       que en la tabla. La valorización (kg) y su % los calcula el backend según
       el tratamiento final: si es de eliminación valen 0 aunque el porcentaje
       del tratamiento final sea 100. Vacía = la línea no tiene ese dato. */
    const pesoEnUnidad = pesoEnUnidadResiduo(
        residuo.peso,
        residuo.codTipoResiduo
    );
    const conDatoValorizacion = residuo.valorizacion != null;
    const porcentajeValorizado = Number(residuo.porcentajeValorizacion) || 0;
    const valorizado = pesoEnUnidadResiduo(
        residuo.valorizacion,
        residuo.codTipoResiduo
    );

    const badge = badgeValidacion(residuo.codEstado);

    const nombreResiduo = [residuo.residuo?.codigo, residuo.residuo?.nombre]
        .filter((p) => p && String(p).trim() !== "" && p !== "-")
        .join(" - ");

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            position="side"
            title={t("wasteDetail.wasteDetails")}
            actions={
                <button
                    className="residuo-detalle__cerrar"
                    onClick={onClose}
                >
                    {t("wasteDetail.close")}
                </button>
            }
        >
            <div className="residuo-detalle">
                <header className="residuo-detalle__cabecera">
                    <h2>{texto(nombreResiduo)}</h2>
                    <div className="residuo-detalle__cabecera-badges">
                        <Badge variant={badge.type} Icon={badge.Icon}>
                            {badge.label}
                        </Badge>
                        <Badge variant={esPeligroso ? "warning" : "info"}>
                            {residuo.residuo?.tipoResiduo?.descripcion ||
                                (esPeligroso
                                    ? "Peligroso"
                                    : "No peligroso")}
                        </Badge>
                    </div>
                    {residuo.lastValidationNote ? (
                        <p className="residuo-detalle__nota-validacion">
                            {residuo.lastValidationNote}
                        </p>
                    ) : null}
                </header>

                <Seccion titulo={t("wasteDetail.waste")}>
                    <Campo label={t("wasteDetail.project")}>
                        {texto(etiqueta(proyectos, residuo.codProyecto))}
                    </Campo>
                    <Campo label={t("wasteDetail.identificationCode")}>
                        {texto(residuo.codIdentificacion)}
                    </Campo>
                    <Campo label={t("wasteDetail.observations")} ancho>
                        {texto(residuo.observacionesGestionFinal)}
                    </Campo>
                </Seccion>

                <Seccion titulo={t("wasteDetail.quantities")}>
                    <Campo label={t("wasteDetail.weight")}>
                        {formatPesoResiduo(
                            residuo.peso,
                            residuo.codTipoResiduo
                        )}
                    </Campo>
                    <Campo label={t("wasteDetail.volume")}>
                        {numero(residuo.volumen, " m³")}
                    </Campo>
                    <Campo label={t("wasteDetail.valorization")}>
                        {conDatoValorizacion
                            ? `${numero(valorizado)} / ${numero(
                                  pesoEnUnidad
                              )} ${unidad} (${numero(
                                  porcentajeValorizado,
                                  "%"
                              )})`
                            : "Sin datos"}
                    </Campo>
                    <Campo label={t("wasteDetail.elimination")}>
                        {residuo.eliminacion == null
                            ? VACIO
                            : formatPesoResiduo(
                                  residuo.eliminacion,
                                  residuo.codTipoResiduo
                              )}
                    </Campo>
                </Seccion>

                <Seccion titulo={t("wasteDetail.transport")}>
                    <Campo label={t("wasteDetail.carrier")} ancho>
                        {texto(residuo.transportista?.nombre)}
                    </Campo>
                    <Campo label={t("wasteDetail.pickupDate")}>
                        {fecha(residuo.fechaTransporte)}
                    </Campo>
                    <Campo label={t("wasteDetail.documentNo")}>
                        {texto(residuo.numDocumentoTransporte)}
                    </Campo>
                    <Campo label={t("wasteDetail.document")} ancho>
                        {documento(
                            residuo.documentoTransporte,
                            residuo.documentoTransporteBase64
                        )}
                    </Campo>
                    <Campo label={t("wasteDetail.vehicleType")}>
                        {texto(etiqueta(vehiculos, residuo.codVehiculo))}
                    </Campo>
                    <Campo label={t("wasteDetail.fuel")}>
                        {texto(
                            etiqueta(combustibles, residuo.codCombustible)
                        )}
                    </Campo>
                    <Campo label={t("wasteDetail.kilometers")}>
                        {numero(residuo.numKm, " km")}
                    </Campo>
                    <Campo label={t("wasteDetail.noOfTrips")}>
                        {numero(residuo.numViajes)}
                    </Campo>
                </Seccion>

                <Seccion titulo={t("wasteDetail.intermediateManagement")}>
                    <Campo label={t("wasteDetail.intermediateManager")} ancho>
                        {texto(residuo.gestorIntermedio?.nombre)}
                    </Campo>
                    <Campo label={t("wasteDetail.date")}>
                        {fecha(residuo.fechaGestionIntermedia)}
                    </Campo>
                    <Campo label={t("wasteDetail.percentage")}>
                        {numero(residuo.porcentaje, "%")}
                    </Campo>
                    <Campo label={t("wasteDetail.treatment")} ancho>
                        {residuo.tipoTratamientoIntermedio
                            ?.codTipoTratamiento
                            ? nombreTratamiento(
                                  residuo.tipoTratamientoIntermedio
                              )
                            : texto(
                                  etiqueta(
                                      tiposTratamiento,
                                      residuo.codTipoTratamiento
                                  )
                              )}
                    </Campo>
                </Seccion>

                <Seccion titulo={t("wasteDetail.finalManagement")}>
                    <Campo label={t("wasteDetail.finalManager")} ancho>
                        {texto(residuo.gestorFinal?.nombre)}
                    </Campo>
                    <Campo label={t("wasteDetail.treatmentDate")}>
                        {fecha(residuo.fechaTratamiento)}
                    </Campo>
                    <Campo label={t("wasteDetail.documentNo")}>
                        {texto(residuo.numDocumentoFinal)}
                    </Campo>
                    <Campo label={t("wasteDetail.document")} ancho>
                        {documento(
                            residuo.documentoFinal,
                            residuo.documentoFinalBase64
                        )}
                    </Campo>
                    <Campo label={t("wasteDetail.percentage")}>
                        {numero(residuo.porcentaje2, "%")}
                    </Campo>
                    <Campo label={t("wasteDetail.treatment")} ancho>
                        {residuo.tipoTratamientoFinal?.codTipoTratamiento
                            ? nombreTratamiento(residuo.tipoTratamientoFinal)
                            : texto(
                                  etiqueta(
                                      tiposTratamiento,
                                      residuo.codTipoTratamiento2
                                  )
                              )}
                    </Campo>
                </Seccion>

                <Seccion titulo={t("wasteDetail.costs")}>
                    <Campo label={t("wasteDetail.transport")}>
                        {importe(residuo.costeTransporte)}
                    </Campo>
                    <Campo label={t("wasteDetail.management")}>
                        {importe(residuo.costeGestion)}
                    </Campo>
                    <Campo label={t("wasteDetail.containerPlacement")}>
                        {importe(residuo.costeColocacion)}
                    </Campo>
                    <Campo label={t("wasteDetail.containerRental")}>
                        {importe(residuo.costeAlquiler)}
                    </Campo>
                    <Campo label={t("wasteDetail.documentGeneration")}>
                        {importe(residuo.costeDocumento)}
                    </Campo>
                    <Campo label={t("wasteDetail.scrapCredit")}>
                        {importe(residuo.abonoChatarra)}
                    </Campo>
                    <div className="residuo-detalle__campo residuo-detalle__campo--ancho residuo-detalle__campo--total">
                        <span className="residuo-detalle__campo-label">
                            {t("wasteDetail.finalCost")}
                        </span>
                        <span
                            className={`residuo-detalle__campo-valor coste ${
                                Number(residuo.costeTotal) < 0
                                    ? "coste--negative"
                                    : "coste--positive"
                            }`}
                        >
                            {importe(residuo.costeTotal ?? 0)}
                        </span>
                    </div>
                </Seccion>
            </div>
        </Modal>
    );
}
